import { FormControl, FormGroup } from '@angular/forms';
import { IDateRangePickerDate } from '../../../shared/components/date-range-picker/date-range-picker.interface';
import { EItemPriority } from '../../../shared/components/item-priority/item-priority.interface';
import { IDropdownOption } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { QuickItemGeneratorComponent } from '../../../shared/components/quick-item-generator/quick-item-generator.component';
import { IBoard, IBoardTeamAssignmentBase, IBoardUserAssignment } from '../../settings/boards/boards.interface';
import { EItemType } from '../../settings/fields-field-sets/field-sets/field-sets.interface';
import { TTeam } from '../../settings/users-teams/teams/teams.interface';
import { MemoryStoredItemAttachmentFile } from '../items-attachments/items-attachments.interface';
import {
  ICustomDateRangeFieldValue,
  ICustomOptionFieldValue,
  ICustomTextFieldValue,
  IItemBase,
  IItemJoins,
  IItemRecurrence,
} from '../items.interface';
import { IItemCategoryOption } from './items-modal.store';
import { IItemLinks } from './link-item/link-item.interface';
import { IRecurringItemFormGroup } from './recurring-item-configuration/recurring-item-configuration.interface';

export type TFormControlItem = {
  assignee: FormControl<IDropdownOption[]>;
  boardId: FormControl<IDropdownOption[]>;
  cronEditor: FormGroup<IRecurringItemFormGroup>;
  description: FormControl<string | null>;
  dueDate: FormControl<IDateRangePickerDate | null>;
  itemCategory: FormControl<IItemCategoryOption[]>;
  itemType: FormControl<IDropdownOption<EItemType>[]>;
  name: FormControl<string>;
  eventDate: FormControl<IDateRangePickerDate | null>;
  priority: FormControl<IDropdownOption<EItemPriority>[] | null>;
  reporter: FormControl<IDropdownOption[] | null>;
  shift: FormControl<IDropdownOption[] | null>;
  status: FormControl<IDropdownOption[] | null>;
};

type TItemBoardItemConfiguration = Pick<IItemJoins, 'boardItemConfiguration'>;
export type TAddItem = Pick<
  IItemBase,
  | 'name'
  | 'description'
  | 'boardId'
  | 'boardItemConfigurationId'
  | 'assigneeId'
  | 'reporterId'
  | 'currentWorkflowStepId'
  | 'dueDate'
  | 'values'
  | 'eventDate'
  | 'shiftId'
  | 'priority'
> &
  TItemBoardItemConfiguration['boardItemConfiguration']['itemType'] & {
    itemRecurrence?: Omit<IItemRecurrence, 'id'>;
  };
export type TEditItem = Partial<
  Pick<
    IItemBase,
    | 'name'
    | 'description'
    | 'assigneeId'
    | 'reporterId'
    | 'currentWorkflowStepId'
    | 'boardItemConfigurationId'
    | 'dueDate'
    | 'values'
    | 'eventDate'
    | 'shiftId'
    | 'priority'
  >
>;

export interface IEditItem {
  dto: TEditItem;
  id: number;
}

export type TCustomFieldFormKey = `${string}_${string}`;

export type TCustomFieldFormValue =
  | ICustomTextFieldValue['selectedValue']
  | ICustomDateRangeFieldValue['selectedValue']
  | ICustomOptionFieldValue['selectedValue'];

export type TFormItem = {
  assignee: IDropdownOption[];
  boardId: IDropdownOption[];
  customFields: Record<TCustomFieldFormKey, TCustomFieldFormValue>;
  description: string;
  itemCategory: IDropdownOption[];
  itemType: IDropdownOption<string>[];
  name: string;
  status: IDropdownOption[];
  eventDate: IDateRangePickerDate | null;
  priority: IDropdownOption<EItemPriority>[];
  shift: IDropdownOption[];
  reporter: IDropdownOption[];
};

export type TItemModalComponentData = {
  idForEdit?: number;
  quickItem?: QuickItemGeneratorComponent['formGroup']['value'] & {
    itemType: IDropdownOption<string>[] | null;
  };
  /** Attachments that are picked in the quick item generator and not uploaded yet */
  quickItemAttachments?: MemoryStoredItemAttachmentFile[];
  selectedBoard?: IDropdownOption | null;
  title?: string;
  /** Create mode only: items the new item is linked to ("relates to") right after it is created. */
  linkedItems?: IItemLinks[];
};

export type TBoardJoins = IBoard & {
  boardTeamAssignments: (IBoardTeamAssignmentBase & { team: TTeam })[];
  boardUserAssignments: IBoardUserAssignment[];
};

export interface IBoardMembersRequest {
  boardId: number;
  search?: string;
  selectedUserId?: number | null;
  escalatedBoardId?: number | null;
}

export interface IGetItemWorkInstructionAssignment {
  boardId: number;
  itemId: number;
}

export const itemFieldsThatCanBeRecursivelyUpdated = [
  'assigneeId',
  'description',
  'boardItemConfigurationId',
  'name',
  'values',
  'shiftId',
  'priority',
] as const;

export type TItemFieldThatCanBeRecursivelyUpdated = (typeof itemFieldsThatCanBeRecursivelyUpdated)[number];

export const itemFieldsThatCannotBeRecursivelyUpdated = ['dueDate'] as const;

export type TItemFieldThatCannotBeRecursivelyUpdated = (typeof itemFieldsThatCannotBeRecursivelyUpdated)[number];
export type TItemUpdateFields = TItemFieldThatCanBeRecursivelyUpdated | TItemFieldThatCannotBeRecursivelyUpdated;
export const itemFieldsFromUrl = [
  'name',
  'assignee',
  'boardId',
  'itemCategory',
  'itemType',
] as const satisfies (keyof TFormControlItem)[];

export interface IItemFieldsFromUrl {
  name: string;
  assignee: number;
  boardId: number;
  itemCategory: number | string;
  itemType: EItemType;
}
